import React from 'react';

import { Text } from '@change/ds-core-ui';
import { TikTokIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { Translate, TranslateHtml } from '@change-corgi/core-react/i18n';

import { SourceIconStack } from '#src/app/pages/homepage/shared/components';

import type { SocialPostErrorCode } from '../../hooks/useSocialPostUrlForm';
import { HELPER_SOURCE_ITEMS } from '../SourceIcon';

type ErrorVariant = 'error' | 'warning';

// Maps each error code to how it should read: the two `warning` codes are pre-submit
// client blocks, the two `error` codes come back from the server on submit.
const ERROR_VARIANT: Readonly<Record<SocialPostErrorCode, ErrorVariant>> = {
	unsupported_platform: 'warning',
	invalid_link: 'warning',
	video_unavailable: 'error',
	generic_error: 'error',
};

// Container styling when no error is shown (the neutral TikTok helper state).
const DEFAULT_CONTAINER = 'bg-fill-information-weaker border-stroke-info-weak/50';

const variantStyles: Readonly<Record<ErrorVariant, { container: string; text: string }>> = {
	error: { container: 'bg-fill-error-weaker border-stroke-error-weak', text: 'text-typography-error' },
	warning: { container: 'bg-fill-warning-weaker border-stroke-warning-success-weak', text: 'text-typography-warning' },
};

// TranslateHtml: each message leads with a <b>-wrapped sentence.
const TIKTOK_ERROR_MESSAGES: Readonly<Record<SocialPostErrorCode, React.ReactNode>> = {
	unsupported_platform: <TranslateHtml value="corgi.homepage.hero_section.social_post_errors.unsupported_platform" />,
	invalid_link: <TranslateHtml value="corgi.homepage.hero_section.social_post_errors.invalid_link" />,
	video_unavailable: <TranslateHtml value="corgi.homepage.hero_section.social_post_errors.video_unavailable" />,
	generic_error: <TranslateHtml value="corgi.homepage.hero_section.social_post_errors.generic_error" />,
};

/** The same codes, worded for a link from any allowlisted source; the generic error needs no rewording. */
const MULTI_SOURCE_ERROR_MESSAGES: Readonly<Record<SocialPostErrorCode, React.ReactNode>> = {
	...TIKTOK_ERROR_MESSAGES,
	unsupported_platform: (
		<TranslateHtml value="corgi.homepage.hero_section.social_post_errors_multi_source.unsupported_platform" />
	),
	invalid_link: <TranslateHtml value="corgi.homepage.hero_section.social_post_errors_multi_source.invalid_link" />,
	video_unavailable: (
		<TranslateHtml value="corgi.homepage.hero_section.social_post_errors_multi_source.link_unavailable" />
	),
};

const NeutralHelper: React.FC<{ multiSource: boolean }> = ({ multiSource }) =>
	multiSource ? (
		<>
			<SourceIconStack items={HELPER_SOURCE_ITEMS} />
			<Text as="p" className="text-typography-weak">
				<Translate value="corgi.homepage.hero_section.input_helper_multi_source" />
				{/* the badge stack is decorative, so the accepted sources are named here for assistive tech */}
				<span className="sr-only" data-qa="source-icon-stack-sources">
					{' '}
					<Translate value="corgi.homepage.hero_section.input_helper_multi_source_sources" />
				</span>
			</Text>
		</>
	) : (
		<>
			<div className="bg-fill-strong border-stroke-inverse-strong elevation-low flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2">
				<TikTokIcon className="text-icon-inverse-strong" size="x-small" />
			</div>
			<Text as="p" className="text-typography-weak">
				<Translate value="corgi.homepage.hero_section.input_helper_tiktok" />
			</Text>
		</>
	);

export const FormHelper: React.FC<Props> = ({ className, error, multiSource = false }) => {
	const errorVariant = error ? ERROR_VARIANT[error] : undefined;
	const errorMessages = multiSource ? MULTI_SOURCE_ERROR_MESSAGES : TIKTOK_ERROR_MESSAGES;

	return (
		<div
			data-qa="social-post-callout"
			className={cn(
				'rounded-large mx-auto -mt-4 flex max-w-[95%] items-center gap-2 border px-5 pt-6 pb-3',
				errorVariant ? variantStyles[errorVariant].container : DEFAULT_CONTAINER,
				errorVariant && 'items-start',
				className,
			)}
		>
			{error && errorVariant ? (
				<>
					<div
						className={cn(
							'm-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
							errorVariant === 'error' ? 'bg-fill-error-strong' : 'bg-fill-warning-strong',
						)}
					>
						<Text variant="body" size="medium" fontWeight="bold" className="text-typography-inverse-strong">
							!
						</Text>
					</div>
					<Text variant="body" size="small" as="p" className={variantStyles[errorVariant].text}>
						{errorMessages[error]}
					</Text>
				</>
			) : (
				<NeutralHelper multiSource={multiSource} />
			)}
		</div>
	);
};

type Props = Readonly<{
	className?: string;
	error?: SocialPostErrorCode;
	/** Word the helper and errors for any allowlisted link, not only TikTok. */
	multiSource?: boolean;
}>;
